// @ts-nocheck
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getFromLocalStorage, setToLocalStorage } from '../utils/localStorage';
import { useOptionalOrganization } from './OrganizationContext';
import type { DraftFinalPayload } from '../utils/draftFinal';
import type { WorkflowCostInfo } from '../utils/workflowCostEstimator';

export interface DraftFinalWorkflow {
  id: string;
  workflow_id: number;
  workflow: WorkflowCostInfo & { settings?: Record<string, unknown> | null };
  dynamic_widget?: unknown;
  [key: string]: unknown;
}

export interface DraftFinalRequest extends DraftFinalPayload {
  sourceSlotId: string;
  workflowId: number;
  workflowTitle: string;
  workflow: DraftFinalWorkflow;
}

export interface GenerationSlot {
  id: string;
  status: 'pending' | 'enhancing' | 'generating' | 'processing' | 'completed' | 'failed';
  prompt: string;
  settings: {
    imageSize: string;
    guidanceScale: number;
    safetyTolerance: string;
    numInferenceSteps: number;
  };
  previewUrl?: string;
  originalUrl?: string;
  // Multiple URLs support for bundled generations
  previewUrls?: string[];
  originalUrls?: string[];
  isFavorite: boolean;
  // Per-asset quality mark (like / dislike / approved); null/undefined = unmarked
  mark?: 'like' | 'dislike' | 'approved' | null;
  error?: string;
  errorDetails?: string;
  timestamp: number;
  // Async job fields
  asyncJobId?: string;
  asyncGenerationId?: number;
  draftJobId?: string;
  // Workflow (model) that produced this slot — used by the Multi section to
  // badge results and map a slot back to its exact model. Numeric workflow id.
  workflowId?: number;
  // Media type - for supporting video, images, etc.
  contentType?: string; // 'video/mp4', 'image/jpeg', 'image/png', 'text', etc.
  // Text content for text-type generations
  textContent?: string;
  // Optional metadata for gallery images
  metadata?: {
    userEmail?: string;
    workflowName?: string;
    createdAt?: string;
    collections?: string;
    originalUrlUnproxied?: string; // Original URL for downloads (not proxied)
    allOriginalUrlsUnproxied?: string[]; // All original URLs for downloads (not proxied)
  };
  // Raw input JSON string from workflow_generations.raw_input (contains prompt_v2 data)
  rawInput?: string;
  // Pipeline progress (multi-step workflow)
  pipelineSteps?: Array<{
    name: string;
    status: 'pending' | 'running' | 'completed' | 'failed' | 'waiting_callback';
  }>;
  currentStepIndex?: number;
  totalSteps?: number;
  // Content policy no-refund flag (from workflow settings)
  noRefundOnContentPolicy?: boolean;
}

export interface WidgetState {
  // Generation Settings Widget
  numImages: number;
  imageSize: string;
  numInferenceSteps: number;
  guidanceScale: number;
  safetyTolerance: string;
  
  // Style Selector Widget
  selectedStyle: string;
  
  // Image Upload Widget
  uploadedImages: File[];
  
  // Input Images Widget
  inputImages: File[];

  // Input Media Widget (files; gallery picks live in the widget's own state)
  inputMedia: File[];

  // Prompt Editor Widget
  prompt: string;
  originalPrompt: string;

  // Prompt Enhancer Widget
  selectedPromptId: string;

  // Big Prompt Editor Widget - Style and Prompt Templates
  selectedBigStyleId: string;
  selectedBigPromptId: string;
  
  // AuraSR Upscale Settings Widget
  auraSrUpscalingFactor: number;
  auraSrOverlappingTiles: boolean;
  auraSrCheckpoint: string;
  
  // Clarity Upscaler Widget
  clarityUpscaleFactor: number;
  clarityResemblance: number;
  
  // Generation Slots for Gallery Widget
  generationSlots: GenerationSlot[];
  
  // Dynamic Widgets State
  dynamicWidgets: {
    [widgetId: string]: {
      [fieldId: string]: any;
    };
  };
}

interface WidgetContextType {
  draftFinalRequest: DraftFinalRequest | null;
  draftFinalConsumerReady: boolean;
  registerDraftFinalConsumer: () => () => void;
  requestDraftFinal: (request: DraftFinalRequest) => void;
  clearDraftFinalRequest: () => void;
  /** Gallery → workbench hand-off: a generation whose final confirmation should open. */
  draftFinalHandoff: { generationId: number } | null;
  startDraftFinalHandoff: (generationId: number) => void;
  clearDraftFinalHandoff: () => void;
  widgetState: WidgetState;
  updateWidgetState: (updates: Partial<WidgetState>) => void;
  resetWidgetState: () => void;
  clearAllSettings: () => void;
  // Generation slots management
  addGenerationSlot: (slot: Omit<GenerationSlot, 'id' | 'timestamp'>) => string;
  updateGenerationSlot: (id: string, updates: Partial<GenerationSlot>) => void;
  clearGenerationSlots: () => void;
  toggleSlotFavorite: (id: string) => void;
  // Dynamic widget state management
  updateDynamicWidgetState: (widgetId: string, fieldValues: Record<string, any>) => void;
  getDynamicWidgetState: (widgetId: string) => Record<string, any>;
  clearDynamicWidgetState: (widgetId: string) => void;
}

const defaultWidgetState: WidgetState = {
  numImages: 1,
  imageSize: 'square_hd',
  numInferenceSteps: 30,
  guidanceScale: 7.0,
  safetyTolerance: '2',
  selectedStyle: '',
  uploadedImages: [],
  inputImages: [],
  inputMedia: [],
  prompt: '',
  originalPrompt: '',
  selectedPromptId: '',
  selectedBigStyleId: 'none',
  selectedBigPromptId: 'none',
  auraSrUpscalingFactor: 4,
  auraSrOverlappingTiles: false,
  auraSrCheckpoint: 'v1',
  clarityUpscaleFactor: 2,
  clarityResemblance: 0.6,
  generationSlots: [],
  dynamicWidgets: {}
};

const WidgetContext = createContext<WidgetContextType | undefined>(undefined);

export const useWidgetContext = (): WidgetContextType => {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error('useWidgetContext must be used within a WidgetProvider');
  }
  return context;
};

interface WidgetProviderProps {
  children: React.ReactNode;
}

export const WidgetProvider: React.FC<WidgetProviderProps> = ({ children }) => {
  // One-shot requests stay in memory and are never part of persisted widget settings.
  const [draftFinalRequest, setDraftFinalRequest] = useState<DraftFinalRequest | null>(null);
  const draftFinalConsumers = useRef(new Set<symbol>());
  const [draftFinalConsumerReady, setDraftFinalConsumerReady] = useState(false);
  const registerDraftFinalConsumer = useCallback(() => {
    const consumer = Symbol();
    draftFinalConsumers.current.add(consumer);
    setDraftFinalConsumerReady(true);
    return () => {
      draftFinalConsumers.current.delete(consumer);
      if (draftFinalConsumers.current.size === 0) {
        setDraftFinalConsumerReady(false);
        setDraftFinalRequest(null);
        // The hand-off is kept: it only opens a confirmation, and StrictMode or a
        // remount re-registers the consumer without re-reading the URL.
      }
    };
  }, []);
  const requestDraftFinal = useCallback((request: DraftFinalRequest) => {
    if (draftFinalConsumers.current.size === 0) throw new Error('Draft final consumer unavailable');
    setDraftFinalRequest(request);
  }, []);
  const clearDraftFinalRequest = useCallback(() => setDraftFinalRequest(null), []);
  // Transient, never persisted: it only opens a confirmation, nothing is billed from it.
  const [draftFinalHandoff, setDraftFinalHandoff] = useState<{ generationId: number } | null>(null);
  const startDraftFinalHandoff = useCallback((generationId: number) => setDraftFinalHandoff({ generationId }), []);
  const clearDraftFinalHandoff = useCallback(() => setDraftFinalHandoff(null), []);

  // Initialize state from localStorage or use defaults
  const [widgetState, setWidgetState] = useState<WidgetState>(() => {
    const savedState = getFromLocalStorage('widget-settings', defaultWidgetState);
    
    return {
      ...savedState,
      uploadedImages: [],
      inputImages: [],
      inputMedia: [],
      generationSlots: [] // Always start with empty slots, will be restored from API
    };
  });

  // No cross-tab sync: localStorage only seeds the initial state of a new tab.
  // A `storage` listener here used to live-merge other tabs' edits, clobbering
  // the prompt while working in two tabs in parallel.

  // Save to localStorage whenever state changes
  useEffect(() => {
    const stateToSave = {
      ...widgetState,
      uploadedImages: [], // Never persist uploaded files
      inputImages: [], // Never persist input images
      inputMedia: [], // Never persist input media files
      generationSlots: [] // Never persist generation slots - they will be restored from API
    };
    setToLocalStorage('widget-settings', stateToSave);
  }, [widgetState]);

  // Drop staged input media when the tenant changes.
  //
  // `inputImages` / `inputMedia` are the files a generation is launched with,
  // and they are picked from (or fetched out of) the CURRENT organization's
  // gallery — via the widget gallery picker or the gallery→workbench "recreate"
  // hand-off. They live here rather than in the widgets, so they outlive the
  // widgets' unmount: staging media in org A, navigating to the gallery,
  // switching to org B and coming back re-seeds org A's media into a generation
  // BILLED to org B, which then stores it in org B's history for org B's
  // managers to read. Same rule (and same reasoning) as the session references
  // in `PromptEditorV2Widget`.
  //
  // Locally-dragged files are dropped too: once staged they are indistinguishable
  // from gallery-derived ones, and re-attaching costs a drag. Ref-guarded rather
  // than a bare dep compare because `currentOrganization` blips through null
  // while the org context refreshes — `5 → null → 5` is not a switch.
  const organizationContext = useOptionalOrganization();
  const currentOrganizationId = organizationContext?.currentOrganization?.id ?? null;
  const stagedMediaOrgRef = useRef<number | null>(null);
  useEffect(() => {
    if (!currentOrganizationId) return;
    if (
      stagedMediaOrgRef.current !== null &&
      stagedMediaOrgRef.current !== currentOrganizationId
    ) {
      // `generationSlots` go too. They are the previous tenant's RESULTS —
      // rendered in the new tenant's workbench, offering a Mark button whose
      // endpoint is tenant-scoped (`WHERE id = ? AND organization_id = ?`), so
      // marking one 404s, and any still-pending slot keeps polling org A's
      // generation with org B's `X-Organization-Id`. Nothing is lost: switching
      // back re-restores in-flight generations from `/api/workflow/active-generations`
      // and finished ones live in that org's gallery.
      setWidgetState(prev => ({
        ...prev,
        uploadedImages: [],
        inputImages: [],
        inputMedia: [],
        generationSlots: []
      }));
      setDraftFinalRequest(null);
      setDraftFinalHandoff(null);
    }
    stagedMediaOrgRef.current = currentOrganizationId;
  }, [currentOrganizationId]);

  const updateWidgetState = useCallback((updates: Partial<WidgetState>) => {
    setWidgetState(prev => ({ ...prev, ...updates }));
  }, []);

  const resetWidgetState = useCallback(() => {
    setWidgetState(defaultWidgetState);
    setDraftFinalRequest(null);
    setDraftFinalHandoff(null);
  }, []);

  const clearAllSettings = useCallback(() => {
    // Import clearAllWidgetSettings dynamically to avoid circular imports
    import('../utils/localStorage').then(({ clearAllWidgetSettings }) => {
      clearAllWidgetSettings();
      setWidgetState(defaultWidgetState);
      setDraftFinalRequest(null);
      setDraftFinalHandoff(null);
    });
  }, []);

  // Generation slots management
  const addGenerationSlot = useCallback((slot: Omit<GenerationSlot, 'id' | 'timestamp'>): string => {
    const id = `slot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newSlot: GenerationSlot = {
      ...slot,
      id,
      timestamp: Date.now()
    };
    
    setWidgetState(prev => ({
      ...prev,
      generationSlots: [...prev.generationSlots, newSlot]
    }));
    
    return id;
  }, []);

  const updateGenerationSlot = useCallback((id: string, updates: Partial<GenerationSlot>) => {
    console.log('🔄 updateGenerationSlot called:', { id, updates });
    setWidgetState(prev => {
      const updatedSlots = prev.generationSlots.map(slot =>
        slot.id === id ? { ...slot, ...updates } : slot
      );
      console.log('📊 Slots after update:', updatedSlots);
      return {
        ...prev,
        generationSlots: updatedSlots
      };
    });
  }, []);

  const clearGenerationSlots = useCallback(() => {
    setWidgetState(prev => ({
      ...prev,
      generationSlots: []
    }));
  }, []);

  const toggleSlotFavorite = useCallback((id: string) => {
    setWidgetState(prev => ({
      ...prev,
      generationSlots: prev.generationSlots.map(slot =>
        slot.id === id ? { ...slot, isFavorite: !slot.isFavorite } : slot
      )
    }));
  }, []);

  // Dynamic widget state management
  const updateDynamicWidgetState = useCallback((widgetId: string, fieldValues: Record<string, any>) => {
    setWidgetState(prev => ({
      ...prev,
      dynamicWidgets: {
        ...prev.dynamicWidgets,
        [widgetId]: fieldValues
      }
    }));
  }, []);

  const getDynamicWidgetState = useCallback((widgetId: string): Record<string, any> => {
    return widgetState.dynamicWidgets[widgetId] || {};
  }, [widgetState.dynamicWidgets]);

  const clearDynamicWidgetState = useCallback((widgetId: string) => {
    setWidgetState(prev => {
      const newDynamicWidgets = { ...prev.dynamicWidgets };
      delete newDynamicWidgets[widgetId];
      return {
        ...prev,
        dynamicWidgets: newDynamicWidgets
      };
    });
  }, []);

  const value: WidgetContextType = {
    draftFinalRequest,
    draftFinalConsumerReady,
    registerDraftFinalConsumer,
    requestDraftFinal,
    clearDraftFinalRequest,
    draftFinalHandoff,
    startDraftFinalHandoff,
    clearDraftFinalHandoff,
    widgetState,
    updateWidgetState,
    resetWidgetState,
    clearAllSettings,
    addGenerationSlot,
    updateGenerationSlot,
    clearGenerationSlots,
    toggleSlotFavorite,
    updateDynamicWidgetState,
    getDynamicWidgetState,
    clearDynamicWidgetState
  };

  return (
    <WidgetContext.Provider value={value}>
      {children}
    </WidgetContext.Provider>
  );
};

export default WidgetContext;
